import { useMemo, useState } from 'react'

import EventsLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import { ZeroState } from '@/components/patterns/ZeroState'
import { PlusCircleFilled } from '@ant-design/icons'
import { HiOutlineBell } from 'react-icons/hi'
import { Button, Pagination, message, Modal } from '@/components/ui'
import { router, usePage } from '@inertiajs/react'

import { FiltersBar } from './components/FiltersBar'
import { PushNotificationsTable } from './components/PushNotificationsTable'
import { SendConfirmModal } from './components/SendConfirmModal'
import type {
  PushNotification,
  PushNotificationFilter,
} from './components/types'

type Event = {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

type PageProps = {
  event: Event
  pushNotifications: PushNotification[]
}

const PAGE_SIZE = 50

const PushNotificationsIndex = () => {
  const { event, pushNotifications = [] } = usePage<PageProps>().props
  const [searchValue, setSearchValue] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [statusFilter, setStatusFilter] =
    useState<PushNotificationFilter>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [selectedNotification, setSelectedNotification] =
    useState<PushNotification | null>(null)
  const [sending, setSending] = useState(false)
  const [recipientModalOpen, setRecipientModalOpen] = useState(false)
  const [recipientNotification, setRecipientNotification] =
    useState<PushNotification | null>(null)

  const filteredNotifications = useMemo(() => {
    const normalizedSearch = appliedSearch.trim().toLowerCase()

    return pushNotifications.filter((notification: PushNotification) => {
      const matchesSearch =
        !normalizedSearch ||
        notification.title.toLowerCase().includes(normalizedSearch)
      const matchesStatus =
        statusFilter === 'all' || notification.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [pushNotifications, appliedSearch, statusFilter])

  const paginatedNotifications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredNotifications.slice(startIndex, startIndex + pageSize)
  }, [filteredNotifications, currentPage, pageSize])

  const handleSearch = () => {
    setAppliedSearch(searchValue)
    setCurrentPage(1)
  }

  const handleReset = () => {
    setSearchValue('')
    setAppliedSearch('')
    setCurrentPage(1)
  }

  const handleCreate = () => {
    router.visit(`/events/${event.slug}/messaging/push_notifications/new`)
  }

  const handleEdit = (notification: PushNotification) => {
    router.visit(`/events/${event.slug}/messaging/push_notifications/${notification.id}/edit`)
  }

  const handleSend = (notification: PushNotification) => {
    setSelectedNotification(notification)
    setSendModalOpen(true)
  }

  const handleConfirmSend = async () => {
    if (!selectedNotification) return

    setSending(true)
    router.post(
      `/events/${event.slug}/messaging/push_notifications/${selectedNotification.id}/send_now`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          message.error('Failed to send push notification.')
        },
        onFinish: () => {
          setSending(false)
          setSendModalOpen(false)
          setSelectedNotification(null)
        },
      }
    )
  }

  const handleDelete = (notification: PushNotification) => {
    if (notification.status === 'sent') {
      return
    }

    const confirmed = window.confirm(
      `Delete the draft "${notification.title}"? This cannot be undone.`
    )
    if (!confirmed) return

    router.delete(
      `/events/${event.slug}/messaging/push_notifications/${notification.id}`,
      {
        preserveScroll: true,
        onError: () => {
          message.error('Failed to delete push notification.')
        },
      }
    )
  }

  const handleViewRecipients = (notification: PushNotification) => {
    setRecipientNotification(notification)
    setRecipientModalOpen(true)
  }

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page)
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setCurrentPage(1)
    }
  }

  const hasNotifications = pushNotifications.length > 0

  return (
    <EventsLayout>
      <PageHeader
        title="Push Notifications"
        subtitle="Create and send text-based notifications via the Event Owner Portal."
        actions={
          <Button
            variant="primary"
            icon={<PlusCircleFilled />}
            onClick={handleCreate}
          >
            Create Push Notifications
          </Button>
        }
      />

      <section className="px-6 py-6">
        {!hasNotifications ? (
          <div className="rounded border border-gray-100 bg-white shadow-sm">
            <ZeroState
              icon={<HiOutlineBell className="text-6xl text-gray-300" />}
              title="No Push Notifications Yet"
              description="Create and send text-based notifications to your event attendees."
              action={{
                label: 'Create Now',
                onClick: handleCreate,
              }}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <FiltersBar
              searchValue={searchValue}
              statusFilter={statusFilter}
              onSearchChange={setSearchValue}
              onStatusChange={(value) => {
                setStatusFilter(value)
                setCurrentPage(1)
              }}
              onSearch={handleSearch}
              onReset={handleReset}
            />

            <PushNotificationsTable
              data={paginatedNotifications}
              onSend={handleSend}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewRecipients={handleViewRecipients}
            />

            {filteredNotifications.length > 0 && (
              <div className="mt-4">
                <Pagination
                  current={currentPage}
                  total={filteredNotifications.length}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </section>

      <SendConfirmModal
        open={sendModalOpen}
        onClose={() => {
          setSendModalOpen(false)
          setSelectedNotification(null)
        }}
        onConfirm={handleConfirmSend}
        message={selectedNotification?.title || ''}
        loading={sending}
      />

      <Modal
        title="Recipients"
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        footer={null}
        width={520}
      >
        <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-100">
          {recipientNotification?.recipients?.length ? (
            recipientNotification.recipients.map((r) => (
              <div key={r.id} className="py-2 text-sm">
                <div className="font-medium text-black/85">{r.name || r.email || `ID ${r.id}`}</div>
                <div className="text-black/60">{r.email}</div>
              </div>
            ))
          ) : (
            <p className="py-2 text-sm text-black/60">
              Recipient details are unavailable for this notification.
            </p>
          )}
        </div>
      </Modal>
    </EventsLayout>
  )
}

export default PushNotificationsIndex
